import type { Messages } from '../messages.js'

/**
 * Japanese. `手` for flips, which is the word a Japanese board game already uses for a move.
 * `もじ` for tiles, after もじぴったん, and `盤` for the board.
 *
 * The interface is ordinary Japanese, kanji and all. Only the tiles are kana, and only because
 * a board cannot deal two thousand kanji.
 *
 * `htBoardBody` carries a sentence the other locales have no need of: on the board, voicing and
 * kana size are not distinctions, so は is played as ば and つ as っ. That is how もじぴったん
 * and every kana crossword work, but a player who has not been told will think the game is
 * broken the first time it refuses がっこう and accepts かつこう.
 */
export const ja: Messages = {
  tag: 'ja',

  readingDictionary: '辞書を読み込んでいます…',
  noWordList: '「{language}」の単語リストがありません。作成してください:  pnpm dictionary build',
  emptyWordList: '「{language}」の単語リストが空です。',

  flips: '手',
  score: '得点',
  words: '単語',
  round: 'ラウンド',
  ticksLeftLabel: 'このラウンドの残り時間',
  typeAWord: '単語を入力',
  tapPrompt: 'もじをタップして選ぶか戻し、それから{action}',

  boardOfTiles: 'もじ{n}枚の盤',
  faceDown: '裏向き',
  wildCard: 'ジョーカー',
  wildKey: 'どのもじでも',
  letterReplaced: '{from}が{to}になりました',
  letterSwap: 'もじが入れ替わった！',
  spentTile: '使用済みのもじ',
  hiddenWhilePaused: '一時停止中は隠されます',
  letterInWord: '{letter}、単語の{position}番目',

  completeWord: '単語を完成',

  completeShort: '完成',
  reset: 'クリア',
  pause: '一時停止',
  resume: '再開',
  newGame: '新しいゲーム',
  paused: '一時停止中',
  outOfFlips: '手がなくなりました',
  finalResult: '{rounds}で{words}、{score}',
  playAgain: 'もう一度遊ぶ',
  share: '共有',
  shareCopied: 'コピーしました。',
  shareSelect: 'これをコピー:',

  lettersSelect: 'もじを選ぶ',
  keysWild: 'どのもじにもない字を入力すると取られます',
  clearsEvery: '選んだ{letter}をすべて消す',
  undoLastLetter: '最後のもじを戻す',
  noWordsYet: 'まだ単語がありません。',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'もう見つけています',
  reasonTooShort: '短すぎます',
  reasonNotAWord: '単語ではありません',
  reasonAllFound: 'すべて見つけています',
  noSuchLetterUp: '表になった{letter}がありません',
  nothingUp: '表になっていません',
  letterHidden: 'もじがかくれました',
  shuffled: 'シャッフルしました',
  shuffledAndBilled: 'シャッフルしました。使わなかった{flips}を差し引きます',
  tooFewLetters: '残りの文字が足りません',

  gameLanguage: '言語',
  interfaceLanguage: '表示',
  dictionarySize: '{full}語のうち日常語{common}語',
  filterLanguages: '言語を検索',
  noMatches: '該当なし',

  nerdMode: 'マニアモード',
  rules: 'ルール',
  difficulty: '難易度',
  difficultyNames: {
    easy: 'やさしい',
    medium: 'ふつう',
    hard: 'むずかしい',
    insane: 'むちゃくちゃ',
  },
  pausedNoBoard: 'このゲームはランキングに載りません。',
  about: 'このゲームについて',
  themeLabel: 'テーマ',
  themeNames: { traditional: '従来', light: '明るい', contrast: '高コントラスト' },
  tiles: 'もじ (N)',
  secondsPerTick: '秒 / 拍',
  holdTicks: '停止の拍',
  minWord: '最短の単語',
  startingFlips: '開始時の手',
  wildChance: 'ジョーカーの確率',
  replaceChance: 'もじが入れ替わる確率',
  hideChance: 'もじがかくれる確率',
  wordCompleteMode: '単語が完成したら',
  wordCompleteNames: { shuffle: 'シャッフル', spend: '使う', keep: '残す' },
  flipEconomy: '手の増え方',
  flipEconomyNames: {
    none: 'なし',
    perLetter: 'もじごと',
    fibonacci: 'フィボナッチ',
    overMinimum: '最短を超えた分',
  },
  repeatedLetterKey: '同じもじのキー',
  keySchemeNames: { cycle: '巡回', advance: '前進' },
  keySchemeHelp: {
    cycle: 'あを押すと未使用のあを次々に取り、すべて単語に入ると消えます。Shift+あでも消えます。',
    advance: 'あを押すと未使用のあを次に取ります。Shift+あは単語の中のあをすべて消します。',
  },

  whatThatMeans: 'その意味',
  factRound: 'ラウンド',
  factWholeBoardUp: '盤全体が見えている時間',
  factRoundCosts: '1ラウンドの費用',
  factFlipsBuy: '開始時の手で買えるもの',
  factThisBoard: 'この盤',
  factBoardHadToAdmit: '盤が満たすべき語数',
  ticksAndSeconds: '{ticks}、{seconds}',
  wordsLongest: '{words}、最長{longest}',
  wordsIncludingOneOf: '{words}、うち1つは{ceiling}',
  scorelessRounds: '{rounds}が無得点',

  whatAWordPays: '単語の値打ち',
  columnLetters: 'もじ',
  columnCost: '費用',
  columnPoints: '得点',
  columnFlips: '手',
  columnNet: '差引',

  canonicalRules: '{difficulty}の標準ルールです。',
  customRules: '既定から変更されています。独自ルールの得点は順位に入りません。',
  applyAndStart: '適用して新しいゲームを開始',
  changesNextGame: '変更は次のゲームから有効になります。',
  presets: '既定:',

  start: 'はじめる',
  restart: 'やり直す',
  quit: 'やめる',
  quitTitle: 'このゲームをやめますか？',
  restartTitle: 'このゲームをやり直しますか？',
  restartConfirm: 'やり直す',
  quitConfirm: 'やめる',
  keepPlaying: '続ける',
  personalBest: 'あなたの最高記録',
  newPersonalBest: '自己ベストを更新しました。',
  columnRank: '#',
  notRanked: '独自ルールのため、このゲームは順位に入りません。',
  notRankedPaused: '時間が止まったため、このゲームは順位に入りません。',
  rankOfTotal: '{total}中{rank}',

  howToPlay: '遊び方',

  backToGame: 'ゲームに戻る',
  welcomeTitle: 'Blinkered へようこそ',
  tutorialSkip: 'スキップ',
  tutorialNext: '次へ',
  tutorialBack: '戻る',
  tutorialStart: 'はじめる',
  tutorialHideAgain: '次から表示しない',
  tutorialProgress: '{total}中{n}',
  tutorialSkipTitle: '説明をスキップしますか？',
  tutGoalTitle: 'Blinkered はレースです',
  tutGoalBody:
    'ゲームは決まった数の手で始まり、もじが1枚表になるごとに1手かかります。手がなくなるとゲームは終わりです。単語を作ると手がもらえ、長い単語ほど多くの手がもらえます。',
  tutSpentBody:
    '単語に使ったもじは、次のラウンドまで裏返しになります。盤のもじが変わるのは、入れ替わりのときだけです。',
  tutChangeTitle: 'もじはかわることがあります',
  tutSameLetters:
    '見つけたのにまにあわなかった単語はありましたか。同じもじが次のラウンドで、別の場所に混ぜられて戻ってきます。',
  tutPickLetters: '使いたいもじを順にタップして単語を作ります。',
  tutMoreTurn: '考えているあいだももじは開き続けるので、もっといいもじがまだ来るかもしれません。',
  tutTapBack:
    '取りたくないものを取ってしまいましたか。もう一度タップすると戻せます。最後の1つだけでなく、どれでも。',
  tutComplete: '単語ができたら完成を押します。',
  htBoardTitle: '盤',
  htBoardBody:
    'もじはランダムな順に1枚ずつ表になります。表になったもじで単語を作ります。盤の上では濁点も半濁点もつけて読めるので、「は」は「ば」として使えます。小さい「っ」「ゃ」「ゅ」「ょ」も大きいもじで表します。',
  htWordsTitle: '単語',
  htWordsBody: '表になったもじを順に入力するかクリックして単語を作ります。',
  htFlipsTitle: '手',
  htFlipsBody:
    'もじが1枚表になるごとに1手かかります。単語が完成すると手が戻り、長い単語ほど多く戻ります。手がなくなるとゲームは終わりです。',
  htRoundTitle: 'ラウンド',
  htRoundBody:
    'そのラウンドの最後のもじが表になると、盤全体がしばらくそのままになります。そのあともじは裏返されて混ぜられ、新しいラウンドが始まります。',
  htLanguagesTitle: '言語',
  htLanguagesBody:
    '{n}言語。どの盤も、人が実際に使う単語で解けます。珍しい単語も辞書にあれば得点になります。',
  htKeysTitle: 'キーボード',
  htWildTitle: 'ジョーカー',
  htWildBody:
    'ときどきもじの代わりにジョーカーが出ます。ジョーカーは正しい単語になるどのもじとしても数えます。すでに完成した単語は数えません。',
  htHideTitle: 'かくれるもじ',
  htHideBody:
    'むずかしいレベルでは、表になっているもじが自分でうら返り、そのラウンドの後であらためて表になります。そのぶんタイマーの目もりが1つふえ、赤で示されます。すでに選んだもじは、うら返りません。',
  tutHideBody:
    'むずかしいレベルでは、表になっているもじが自分でうら返り、そのラウンドの後であらためて表になります。すでに選んだもじは、うら返りません。',
  htSwapTitle: 'もじの入れ替わり',
  htSwapBody:
    'ときどきラウンドの合間に、1枚のもじが別のもじに入れ替わります。どのもじが消えてどのもじが増えたか分かります。',
  htLevelsTitle: '難易度',
  htLevelEasy:
    'ゲーム中ずっと同じ12枚なので、覚えて頭の中に単語のリストを持てます。もじはゆっくり表になり、盤全体は選び終わるまで十分に見えています。',
  htLevelMedium:
    'ときどき1枚が変わるので、あとで使うつもりだった単語を覚えておくのが難しくなります。見る時間も考える時間も少なくなります。',
  htLevelHard:
    '3もじの単語は数えなくなり、2ラウンドに1度ほどもじが変わります。盤は開いたと思うともう混ざります。',
  htLevelInsane: 'すべて同時に、最高速度で。最後の1手のすぐあとに盤が混ざります。',
  htTouchTitle: 'タッチ画面',
  htTouchBody:
    '表になったもじをタップするとその字を取ります。取ったもじをタップすると戻せます。完成とクリアは盤の下にあります。',

  accountTitle: 'アカウント',
  menuProfile: 'プロフィール',
  menuGames: 'プレイ履歴',
  menuPublicPage: '公開ページ',
  menuSignedInAs: 'ログイン中',
  signIn: 'ログイン',
  signOut: 'ログアウト',
  signInTitle: 'ログインまたは登録',
  signInLead: 'アカウントがあれば、どの端末でもゲームが残ります。',
  signInKeepGame: 'ログインすると、このゲームもこれからのゲームも残ります。',
  continueWithApple: 'Appleで続ける',
  continueWithGoogle: 'Googleで続ける',
  signInOr: 'または',
  emailLabel: 'メール',
  codeLabel: 'コード',
  emailMeACode: 'コードを送る',
  working: '処理中…',
  sendNewCode: '新しいコードを送る',
  codeSent: 'メールを確認してください。コードは10分間有効です。',
  notNow: 'あとで',
  badEmail: 'メールアドレスではないようです。',
  badCode: 'このコードは使えませんでした。コードは10分で失効し、一度だけ使えます。',
  serverBusy: 'サーバーに接続できませんでした。少し待って試してください。',
  serverDown: 'サーバーに接続できませんでした。',
  offline: 'オフライン',
  leaderboardTitle: 'ランキング',
  leaderboardEmpty: 'このランキングにはまだ記録がありません。最初の一人になりましょう。',
  leaderboardWouldBe: 'この記録が入る位置',
  leaderboardThisGame: 'この記録',
  leaderboardRanksHere: 'この記録が入った位置',
  leaderboardOutside: '上位5位圏外です。',
  tutAccountTitle: 'スコアを残す',
  tutAccountBody:
    'アカウントがあれば、記録が保存され、スコアがランキングに載り、プロフィールを管理できます。',
  ssoCancelled: 'ログインを中止しました。',
  ssoExpired: '時間がかかりすぎました。もう一度ログインしてください。',
  ssoNoUsername: 'アカウントを作成しきれませんでした。少し待って試してください。',
  ssoFailed: 'うまくいきませんでした。もう一度試すか、メールを使ってください。',
  tabProfile: 'プロフィール',
  countryAny: '答えない',
  findCountry: '国を検索',
  usernameLabel: 'ユーザー名',
  bioLabel: '自己紹介',
  countryLabel: '国',
  bioHint: 'リンクは不可。{left}/{max}',
  save: '保存',
  saving: '保存中…',
  saved: '保存しました。',
  nameTaken: 'その名前は使われています。',
  nameTooShort: 'もう少し長くしてください。',
  nameTooLong: '長すぎます。',
  nameBadCharacters: '使えるのは英数字、ハイフン、アンダースコアだけです。',
  nameBadEdges: '先頭と末尾は文字か数字にしてください。',
  nameMixedScripts: '文字体系は一つに揃えてください。',
  nameReserved: 'こちらが配る名前に似ています。別の名前にしてください。',
  nameUnusable: 'その名前は使えません。',
  bioTooLong: '自己紹介は{max}文字までです。',
  bioHasLink: '自己紹介にリンクは書けません。',
  bioHasControl: '自己紹介に入れられない文字が含まれています。',
  bioUnusable: 'その自己紹介は使えません。',
  gamesHeading: 'ゲーム',
  gamesEmpty: 'まだ何もありません。ログイン中に遊んだゲームがここに並びます。',
  gamesEmptyShort: 'まだ何もありません。',
  gamesLoading: 'ゲームを読み込み中…',
  theirGamesLoading: 'このプレイヤーのゲームを読み込み中…',
  profileLoading: 'プロフィールを読み込み中…',
  playerNotFound: 'その名前の人はいません。',
  columnWhen: 'いつ',
  columnRounds: 'ラウンド',
  columnGame: 'ゲーム',
  gameLoading: 'ゲームを読み込み中…',
  gameUnreadable: 'このゲームは読み込めませんでした。',
  gameNotRanked: 'ルールを変えて遊んだゲームなので、順位はつきません。',
  detailNotKept: 'このゲームの単語と盤面はもう保存されていません。',
  openingBoard: '最初の盤面',
  roundByRound: 'ラウンドごと',
  roundNothing: '見つからず',
  changeReplaced: '{slot}番目が{letter}に入れ替わりました',
  changeWildOn: '{slot}番目の{letter}がワイルドに',
  changeWildOff: '{slot}番目の{letter}のワイルドが外れました',
  keepThisGame: 'このゲームを残す',
  savedToYourGames: 'ゲームに保存しました。',

  reportThis: '報告',
  reportHeading: 'これを報告する',
  reportTheName: '名前',
  reportTheBio: '自己紹介',
  reportTheScore: 'スコア',
  reportWhy: 'どこが問題ですか？（任意）',
  reportSend: '報告を送る',
  reportSent: 'ありがとうございます。誰かが確認します。',
  reportSignIn: '報告するにはログインしてください。',
  reportGone: 'それはもうありません。',
  reportCancel: 'キャンセル',

  deleteAccount: 'アカウントを削除',
  deleteAccountWhat: 'プロフィールと保存したすべてのゲームを完全に削除します。取り消せません。',
  deleteAccountAsk: 'コードを送る',
  deleteConfirm: '完全に削除する',
  deleteGone: 'アカウントを削除しました。遊んでくれてありがとう。',
  deleteNoAddress:
    'このアカウントには確認済みのアドレスがないため、削除を確認できません。ご連絡いただければ手作業で対応します。',

  rulesetFacts: '{letters}の単語 · {seconds}ごとにもじが1枚表になる',
  plurals: {
    words: { other: '{n}語' },
    rounds: { other: '{n}ラウンド' },
    flips: { other: '{n}手' },
    ticks: { other: '{n}拍' },
    points: { other: '{n}点' },
    minLetters: { other: '{n}もじ以上' },
    seconds: { other: '{n}秒' },
  },
}
